import axios, { AxiosError, AxiosResponse, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import NProgress from 'nprogress';
import { serverBaseUrl } from '../constants/defaultValues';

export interface ApiErrorPayload {
  msg?: string;
  message?: string;
  error?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  status?: number;

  data?: ApiErrorPayload;

  constructor(message: string, status?: number, data?: ApiErrorPayload) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

interface RetriableConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

interface RefreshResponse {
  accessToken: string;
  refreshToken?: string;
}

const axiosInstance = axios.create({
  baseURL: serverBaseUrl,
  timeout: 30000,
});

let inflight = 0;
const bumpInflight = (): void => {
  if (inflight === 0) NProgress.start();
  inflight += 1;
};
const dropInflight = (): void => {
  inflight = Math.max(0, inflight - 1);
  if (inflight === 0) NProgress.done();
};

const enrich = (error: AxiosError<ApiErrorPayload>): ApiError => {
  const data = error.response?.data;
  const message =
    data?.msg || data?.message || data?.error || error.message || 'Something went wrong';
  return new ApiError(message, error.response?.status, data);
};

const isAuthRoute = (url = ''): boolean =>
  url.includes('/v1/auth/login') ||
  url.includes('/v1/auth/register') ||
  url.includes('/v1/auth/refresh');

const setAuthHeader = (token: string | null): void => {
  if (token) {
    axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete axiosInstance.defaults.headers.common.Authorization;
  }
};

const clearSessionAndRedirect = (): void => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('authUser'); // cached user for optimistic boot (JWTContext)
  setAuthHeader(null);
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth/')) {
    window.location.href = '/auth/login';
  }
};

// Keep every tab's in-memory Authorization header in lockstep with localStorage.
// When one tab refreshes (or logs in/out), the others pick up the fresh token
// without first having to eat their own 401.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === 'accessToken') setAuthHeader(event.newValue);
  });
}

// Serialize token refresh. The Web Locks API coordinates ACROSS tabs so we never
// present the same single-use refresh token twice (which the backend treats as
// token reuse and punishes by revoking the whole session family). Falls back to
// an in-tab promise chain where Web Locks is unavailable.
let localLock: Promise<unknown> = Promise.resolve();
const withRefreshLock = <T,>(fn: () => Promise<T>): Promise<T> => {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request('wesafe-token-refresh', fn) as Promise<T>;
  }
  const run = localLock.then(fn, fn);
  localLock = run.then(
    () => undefined,
    () => undefined
  );
  return run;
};

// Runs inside the refresh lock. `staleToken` is the access token the failing
// request used; if localStorage now holds a different one, another request/tab
// already refreshed while we waited for the lock — reuse it instead of spending
// (and burning) the refresh token again.
const refreshAccessToken = async (staleToken: string | null): Promise<string> => {
  const current = localStorage.getItem('accessToken');
  if (current && current !== staleToken) {
    setAuthHeader(current);
    return current;
  }

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) throw new ApiError('No refresh token', 401);

  const { data } = await axios.post<RefreshResponse>(
    `${serverBaseUrl}/v1/auth/refresh`,
    { refreshToken },
    { timeout: 15000 }
  );
  const newAccess = data.accessToken;
  const newRefresh = data.refreshToken || refreshToken;
  localStorage.setItem('accessToken', newAccess);
  localStorage.setItem('refreshToken', newRefresh);
  setAuthHeader(newAccess);
  return newAccess;
};

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    bumpInflight();
    return config;
  },
  (error: AxiosError<ApiErrorPayload>) => {
    dropInflight();
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    dropInflight();
    return response;
  },
  async (error: AxiosError<ApiErrorPayload>) => {
    dropInflight();
    const original = (error.config || {}) as RetriableConfig;
    const status = error.response?.status;

    if (status === 401 && !original._retry && !isAuthRoute(original.url)) {
      const refreshToken = localStorage.getItem('refreshToken');

      // Impersonation token expired — restore admin session automatically.
      if (!refreshToken && localStorage.getItem('adminAccessToken')) {
        const adminAccess = localStorage.getItem('adminAccessToken')!;
        const adminRefresh = localStorage.getItem('adminRefreshToken');
        localStorage.setItem('accessToken', adminAccess);
        if (adminRefresh) localStorage.setItem('refreshToken', adminRefresh);
        setAuthHeader(adminAccess);
        localStorage.removeItem('adminAccessToken');
        localStorage.removeItem('adminRefreshToken');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('impersonationLogId');
        if (typeof window !== 'undefined') {
          window.location.href = '/dashboard/admin/users';
        }
        return Promise.reject(enrich(error));
      }

      if (!refreshToken) return Promise.reject(enrich(error));

      original._retry = true;
      const staleToken = localStorage.getItem('accessToken');

      try {
        const newAccess = await withRefreshLock(() => refreshAccessToken(staleToken));
        original.headers = {
          ...(original.headers || {}),
          Authorization: `Bearer ${newAccess}`,
        } as AxiosRequestConfig['headers'];
        return await axiosInstance(original);
      } catch (refreshErr) {
        const axiosErr = refreshErr as AxiosError<ApiErrorPayload>;
        const refreshStatus = (refreshErr as ApiError).status ?? axiosErr.response?.status;
        // Only end the session on a DEFINITIVE auth rejection from /refresh:
        //   400 — malformed request, 401 — refresh token invalid/expired/revoked.
        // Transient failures (network error, 5xx, timeout) must NOT bounce the
        // user to login — surface them so the caller can retry with the session
        // still intact.
        if (refreshStatus === 400 || refreshStatus === 401) {
          clearSessionAndRedirect();
        }
        return Promise.reject(enrich(axiosErr));
      }
    }

    return Promise.reject(enrich(error));
  }
);

export default axiosInstance;
